/**
 * Branded transactional / marketing email shell — inline CSS for client compatibility.
 */

import { MAIL_TEMPLATE_KEYS } from './mail-templates';

export type MailFooterMode = 'auth' | 'transactional' | 'notification' | 'marketing';

export type MailBrandConfig = {
  logoUrl: string;
  appUrl: string;
  preferencesUrl?: string;
};

export type BrandedMailLayoutOptions = MailBrandConfig & {
  footerMode?: MailFooterMode;
};

export const LOAMIFY_MAIL_FOOTER_MARKER = '<!-- LOAMIFY_MAIL_FOOTER -->';

export const DEFAULT_MAIL_LOGO_URL = 'https://loamify.io/brand/loamify-wordmark.png';
export const DEFAULT_MAIL_APP_URL = 'https://loamify.io';

const BRAND = {
  ink: '#1a1d21',
  inkSoft: '#999999',
  background: '#f4f4f5',
  card: '#ffffff',
  border: '#d6d6d6',
  primary: '#1a1d21',
  primaryText: '#ffffff',
  link: '#1a1d21',
};

export const MAIL_SOCIAL_LINKS = [
  { label: 'X', href: 'https://x.com/loamify' },
  { label: 'Instagram', href: 'https://instagram.com/loamify' },
  { label: 'LinkedIn', href: 'https://linkedin.com/company/loamify' },
] as const;

export function resolveMailBrandConfig(env?: {
  appUrl?: string;
  logoUrl?: string;
  preferencesUrl?: string;
}): MailBrandConfig {
  const appUrl = (env?.appUrl ?? process.env.MAIL_WEB_BASE_URL ?? DEFAULT_MAIL_APP_URL).replace(
    /\/$/,
    '',
  );
  const logoUrl =
    env?.logoUrl ??
    process.env.MAIL_LOGO_URL ??
    `${appUrl}/brand/loamify-wordmark.png`;
  const preferencesUrl =
    env?.preferencesUrl ?? `${appUrl}/settings/notifications/in-app`;
  return { logoUrl, appUrl, preferencesUrl };
}

function escapeAttr(value: string) {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function mailFooterModeForTemplateKey(key: string): MailFooterMode {
  if (key.startsWith('auth.')) return 'auth';
  if (key.startsWith('platform.') || key.startsWith('custom.')) return 'marketing';
  if (
    key.startsWith('notification.') ||
    key === 'community.broadcast' ||
    (key.startsWith('course.') &&
      key !== MAIL_TEMPLATE_KEYS.COURSE_PURCHASE_RECEIPT)
  ) {
    return 'notification';
  }
  return 'transactional';
}

export function buildMailFooterHtml(options: BrandedMailLayoutOptions): string {
  const appUrl = options.appUrl.replace(/\/$/, '');
  const mode = options.footerMode ?? 'transactional';
  const prefs = options.preferencesUrl ?? `${appUrl}/settings/notifications/in-app`;

  const legalLinks = [
    `<a href="${escapeAttr(`${appUrl}/terms`)}" style="color:${BRAND.inkSoft};text-decoration:underline;">Terms</a>`,
    `<a href="${escapeAttr(`${appUrl}/privacy`)}" style="color:${BRAND.inkSoft};text-decoration:underline;">Privacy</a>`,
    `<a href="${escapeAttr(`${appUrl}/help`)}" style="color:${BRAND.inkSoft};text-decoration:underline;">Help</a>`,
  ].join(' &nbsp;·&nbsp; ');

  const socialLinks = MAIL_SOCIAL_LINKS.map(
    (s) =>
      `<a href="${escapeAttr(s.href)}" style="color:${BRAND.inkSoft};text-decoration:none;margin:0 8px;">${s.label}</a>`,
  ).join('');

  let disclaimer = '';
  if (mode === 'auth') {
    disclaimer =
      '<p style="margin:0 0 12px;font-size:12px;line-height:1.5;color:#888;">If you did not request this email, you can safely ignore it. For account security, never share verification codes with anyone.</p>';
  } else if (mode === 'notification') {
    disclaimer = `<p style="margin:0 0 12px;font-size:12px;line-height:1.5;color:#888;">Manage notification preferences: <a href="${escapeAttr(prefs)}" style="color:${BRAND.link};">Notification settings</a></p>`;
  } else if (mode === 'marketing') {
    disclaimer = `<p style="margin:0 0 12px;font-size:12px;line-height:1.5;color:#888;">You received this because you opted in to product updates from Loamify. <a href="${escapeAttr(prefs)}" style="color:${BRAND.link};">Unsubscribe</a> or update your preferences anytime.</p>`;
  } else {
    disclaimer =
      '<p style="margin:0 0 12px;font-size:12px;line-height:1.5;color:#888;">This is a transactional message about your Loamify account.</p>';
  }

  const address =
    '<p style="margin:12px 0 0;font-size:11px;line-height:1.5;color:#aaa;">Loamify · Product updates &amp; community platform</p>';

  return `
    <tr>
      <td style="padding:24px 32px 28px;border-top:1px solid ${BRAND.border};background:${BRAND.card};">
        ${disclaimer}
        <p style="margin:0 0 8px;font-size:12px;line-height:1.5;color:${BRAND.inkSoft};">${legalLinks}</p>
        <p style="margin:0 0 8px;font-size:12px;line-height:1.5;">${socialLinks}</p>
        ${address}
      </td>
    </tr>`;
}

export function mailFooterPlainText(mode: MailFooterMode, appUrl: string, preferencesUrl: string): string {
  const base = appUrl.replace(/\/$/, '');
  const lines: string[] = [''];
  if (mode === 'auth') {
    lines.push('If you did not request this email, you can safely ignore it.');
  } else if (mode === 'notification') {
    lines.push(`Manage notification preferences: ${preferencesUrl}`);
  } else if (mode === 'marketing') {
    lines.push(`Unsubscribe or update preferences: ${preferencesUrl}`);
  }
  lines.push(`${base}/terms · ${base}/privacy · ${base}/help`);
  return `\n${lines.join('\n')}`;
}

/** Inject or replace footer in layout HTML based on template type. */
export function injectMailLayoutFooter(
  html: string,
  templateKey: string,
  brand: MailBrandConfig,
): string {
  const footerMode = mailFooterModeForTemplateKey(templateKey);
  const footerHtml = buildMailFooterHtml({
    ...brand,
    footerMode,
    preferencesUrl: brand.preferencesUrl,
  });

  if (html.includes(LOAMIFY_MAIL_FOOTER_MARKER)) {
    return html.replace(LOAMIFY_MAIL_FOOTER_MARKER, footerHtml);
  }

  // Legacy layouts: replace last footer row (border-top on td) before inner table closes
  const legacyFooterPattern =
    /<tr>\s*<td style="padding:24px 32px 28px;border-top:1px solid[^"]*;"[\s\S]*?<\/tr>\s*(?=<\/table>)/i;
  if (legacyFooterPattern.test(html)) {
    return html.replace(legacyFooterPattern, footerHtml);
  }

  return html.replace('</table>', `${footerHtml}</table>`);
}

const LEGACY_MAIL_LOGO_URLS = [
  DEFAULT_MAIL_LOGO_URL,
  'http://localhost:3000/brand/loamify-wordmark.png',
  'http://localhost:3002/brand/loamify-wordmark.png',
  'http://localhost:3003/brand/loamify-wordmark.png',
  'https://loamify.io/brand/loamify-wordmark.png',
] as const;

/** Apply current brand logo + home URL to layout HTML (placeholders or legacy baked-in URLs). */
export function applyMailBrandToLayoutHtml(html: string, brand: MailBrandConfig): string {
  const appUrl = brand.appUrl.replace(/\/$/, '');
  let next = html
    .replace(/\{\{logoUrl\}\}/g, brand.logoUrl)
    .replace(/\{\{appUrl\}\}/g, appUrl);

  for (const legacy of LEGACY_MAIL_LOGO_URLS) {
    if (legacy !== brand.logoUrl) {
      next = next.split(legacy).join(brand.logoUrl);
    }
  }

  next = next.replace(
    /(<img[^>]*alt="Loamify"[^>]*src=")[^"]+(")/i,
    `$1${escapeAttr(brand.logoUrl)}$2`,
  );
  next = next.replace(
    /(<img[^>]*src=")[^"]+("[^>]*alt="Loamify")/i,
    `$1${escapeAttr(brand.logoUrl)}$2`,
  );
  next = next.replace(
    /(<a[^>]*href=")[^"]+("[^>]*style="text-decoration:none;display:inline-block;"\s*>\s*<img[^>]*alt="Loamify")/i,
    `$1${escapeAttr(appUrl)}$2`,
  );

  return next;
}

/** True when published layout predates branded shell (no logo img + dynamic footer marker). */
export function isLegacyMailLayoutHtml(html: string): boolean {
  const trimmed = html.trim();
  if (!trimmed) return true;
  if (trimmed.includes(LOAMIFY_MAIL_FOOTER_MARKER)) return false;
  if (trimmed.includes('{{logoUrl}}') && trimmed.includes('alt="Loamify"')) return false;
  if (/<img[^>]*alt="Loamify"/i.test(trimmed)) return false;
  return true;
}

/** Prefer published layout HTML; fall back to branded code default when legacy or missing. */
export function resolveMailLayoutHtml(
  dbHtml: string | null | undefined,
  brand?: MailBrandConfig,
): string {
  if (dbHtml && !isLegacyMailLayoutHtml(dbHtml)) return dbHtml;
  return buildBrandedMailLayoutHtml(brand ?? resolveMailBrandConfig());
}

/** Default system.layout HTML — footer injected at render time via marker. */
export function buildBrandedMailLayoutHtml(options: Partial<BrandedMailLayoutOptions> = {}): string {
  resolveMailBrandConfig(options);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8"/>
    <meta name="viewport" content="width=device-width, initial-scale=1"/>
    <meta name="color-scheme" content="light"/>
    <title>{{title}}</title>
  </head>
  <body style="margin:0;padding:0;background:${BRAND.background};font-family:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:${BRAND.ink};">
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:${BRAND.background};padding:32px 16px;">
      <tr>
        <td align="center">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:600px;background:${BRAND.card};border:1px solid ${BRAND.border};border-radius:16px;overflow:hidden;">
            <tr>
              <td style="padding:28px 32px 8px;background:${BRAND.card};">
                <a href="{{appUrl}}" style="text-decoration:none;display:inline-block;">
                  <img src="{{logoUrl}}" alt="Loamify" width="140" height="32" style="display:block;border:0;max-width:140px;height:auto;"/>
                </a>
              </td>
            </tr>
            <tr>
              <td style="padding:8px 32px 4px;">
                <h1 style="margin:0;font-size:22px;font-weight:700;line-height:1.3;color:${BRAND.ink};">{{title}}</h1>
              </td>
            </tr>
            <tr>
              <td style="padding:12px 32px 28px;font-size:15px;line-height:1.65;color:${BRAND.ink};">
                {{bodyHtml}}
              </td>
            </tr>
            ${LOAMIFY_MAIL_FOOTER_MARKER}
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`;
}

/** Inline CTA pill matching social app primary button. */
export function mailCtaButtonHtml(label: string, href: string): string {
  return `<a href="${escapeAttr(href)}" style="display:inline-block;padding:12px 22px;background:${BRAND.primary};color:${BRAND.primaryText};text-decoration:none;border-radius:999px;font-size:14px;font-weight:600;line-height:1;">${escapeHtml(label)}</a>`;
}
